// Info hotspot → modal renderer.
//
// Built as DOM rather than an HTML string. The old version interpolated the
// description into `bodyHtml` and hand-rolled an escaper; once `imageUrl`
// arrived — a URL that on a portal-authored tour comes straight from Supabase
// and was typed by an admin — string-building an <img> tag would have made
// this the one place in the viewer where config text reaches innerHTML with an
// attribute boundary in it. textContent and .src assignment have no such edge.

import type { InfoHotspot } from '../app/types';
import type { ModalRoot } from './modal';

export function openInfoModal(modal: ModalRoot, hotspot: InfoHotspot): void {
  const body = document.createElement('div');

  if (hotspot.imageUrl) {
    body.appendChild(buildImage(hotspot));
  }

  const p = document.createElement('p');
  p.textContent = hotspot.description;
  body.appendChild(p);

  modal.open({
    id: `info:${hotspot.id}`,
    title: hotspot.label,
    bodyNode: body,
  });
}

/**
 * A figure that removes itself on error. A 404 or an expired signed URL
 * otherwise leaves a broken-image glyph sitting above the caption, which reads
 * as a bug in the tour rather than a missing file.
 */
function buildImage(hotspot: InfoHotspot): HTMLElement {
  const figure = document.createElement('figure');
  figure.className = 'modal__figure';

  const img = document.createElement('img');
  img.className = 'modal__image';
  img.src = hotspot.imageUrl ?? '';
  img.alt = hotspot.imageAlt ?? hotspot.label;
  img.loading = 'lazy';
  img.decoding = 'async';
  img.addEventListener('error', () => figure.remove(), { once: true });

  figure.appendChild(img);
  return figure;
}
